"use client";

import { useState } from "react";
import Link from "@/components/ui/link";
import { useTina, tinaField, coloredText, getIcon } from "@/lib/utils";

import axios from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import Section from "@/components/Section";
import { Button } from "@/components/ui/button";

import { Loader2 } from "lucide-react";
import CallToAction from "@/components/CallToAction";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("react-lottie-player"), { ssr: false });

import animationData from "@/assets/success.json";

export default function ContactPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, contactForm, callToAction } = data.contact;
  return (
    <div className="flex-1 flex flex-col">
      <Section
        wrapperClassName="flex-1 bg-gradient-to-b from-background-blackish via-background-blackish to-background-purplish"
        className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-12 lg:gap-20 items-start"
      >
        <Header header={header} />
        <ContactForm contactForm={contactForm} />
      </Section>
      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <div className="flex flex-col gap-4 justify-center">
      <h1
        data-tina-field={tinaField(header, "title")}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <p
        data-tina-field={tinaField(header, "description")}
        className="p-xl text-foreground-300"
      >
        {header?.description}
      </p>
      <div className="flex flex-col items-start gap-4">
        {header?.contactDetails?.map((detail, index) => {
          const Icon = getIcon(detail.icon);
          return (
            <Button key={index} variant="link" asChild>
              <Link href={detail.url}>
                <div className="flex items-center gap-4">
                  {Icon && <Icon className="text-primary-300 size-6" />}
                  <p
                    className="p-lg"
                    data-tina-field={tinaField(detail, "label")}
                  >
                    {detail.label}
                  </p>
                </div>
              </Link>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

const formSchema = z.object({
  fullName: z.string().min(1, "Required"),
  email: z.string().email({
    message: "Invalid email",
  }),
  company: z.string().min(1, "Required"),
  reason: z.string().min(1, "Required"),
  message: z.string().min(1, "Required"),
});

const ContactForm = ({ contactForm }) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      company: "",
      reason: "",
      message: "",
    },
  });

  const [isSuccess, setIsSuccess] = useState(false);

  async function onSubmit(values) {
    try {
      await axios.post(`/api/email`, {
        values,
        template: "contact",
      });
      setIsSuccess(true);
      form.reset();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.details?.error?.details?.message ||
        error.response?.data?.details?.message ||
        "Something went wrong. Please try again.";
      form.setError("root", {
        type: "manual",
        message: errorMessage,
      });
    }
  }

  return (
    <div className="card !p-6 lg:!p-10 bg-background-purplish relative">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 text-foreground-100 [&_label]:mb-1"
        >
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Full Name
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Email
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input placeholder="john@doe.com" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Company
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input placeholder="Acme Inc." {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Reason
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Select
                    className="cursor-pointer"
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger
                      className="cursor-pointer w-full border-input font-normal text-base text-white placeholder:text-muted-foreground placeholder:text-base h-12 shadow-none hover:border-primary-200/90 hover:bg-transparent hover:text-muted-foreground focus:border-primary-200/90 focus-visible:border-primary-200/90"
                      aria-label="Reason"
                    >
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent className="cursor-pointer bg-background-purplish text-foreground-100 border-input">
                      <SelectItem value="bug-report">Bug Report</SelectItem>
                      <SelectItem value="billing-issue">
                        Billing Issue
                      </SelectItem>
                      <SelectItem value="question">Question</SelectItem>
                      <SelectItem value="support-request">
                        Support Request
                      </SelectItem>
                      <SelectItem value="subject-access-request">
                        Subject Access Request
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="flex-1">
                      Message
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-24 p-4"
                      placeholder="I'm contacting you because..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:col-span-2 w-full">
            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="animate-spin size-5" />
              ) : (
                "Submit"
              )}
            </Button>
            {form.formState.errors.root && (
              <div className="text-red-500 text-sm font-medium text-center capitalize mt-4">
                {form.formState.errors.root.message}
              </div>
            )}
          </div>
          {isSuccess && (
            <SuccessMessage successMessage={contactForm?.successMessage} />
          )}
        </form>
      </Form>
    </div>
  );
};

const SuccessMessage = ({ successMessage }) => {
  return (
    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-background-purplish rounded-lg text-center">
      <div className="w-48 min-h-48">
        <Lottie
          loop={false}
          play
          animationData={animationData}
          className="success-animation w-48 h-48"
        />
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <div
          className="h4 mb-3"
          data-tina-field={tinaField(successMessage, "title")}
        >
          {successMessage?.title || "Your message has been sent successfully!"}
        </div>
        <div
          className="p-lg max-w-[500px] text-foreground-500"
          data-tina-field={tinaField(successMessage, "subtitle")}
        >
          {successMessage?.subtitle ||
            "We will get back to you as soon as possible."}
        </div>
      </div>
    </div>
  );
};
